/**
 * Estimateur Module
 *
 * Interactive price estimator with 4-step wizard:
 * 1. Project type  2. Ambition level  3. Features  4. Result + lead capture
 *
 * Pricing is indicative — the goal is to capture the lead's email/phone.
 *
 * @module Estimateur
 */
export default class Estimateur {

  static DEFAULTS = {
    selector: '[data-estimateur]',
  };

  // Base pricing matrix: [min, max] per type × ambition.
  static PRICING = {
    vitrine:     { essentiel: [2500, 4000],  premium: [4000, 7000],   surmesure: [7000, 12000] },
    ecommerce:   { essentiel: [5000, 8000],  premium: [8000, 15000],  surmesure: [15000, 25000] },
    application: { essentiel: [8000, 15000], premium: [15000, 30000], surmesure: [30000, 60000] },
    refonte:     { essentiel: [2000, 5000],  premium: [5000, 10000],  surmesure: [10000, 20000] },
  };

  constructor() {
    this.root = document.querySelector(Estimateur.DEFAULTS.selector);
    if (!this.root) return;

    this.currentStep = 1;
    this.choices = { type: null, ambition: null, features: [] };

    this._cacheDOM();
    this._bindEvents();
  }

  _cacheDOM() {
    this.panels     = this.root.querySelectorAll('[data-panel]');
    this.indicators = this.root.querySelectorAll('[data-step-indicator]');
    this.backBtn    = this.root.querySelector('[data-estimateur-back]');
    this.priceMin   = this.root.querySelector('[data-price-min]');
    this.priceMax   = this.root.querySelector('[data-price-max]');
    this.summaryEl  = this.root.querySelector('[data-estimateur-summary]');
    this.form       = this.root.querySelector('[data-estimateur-form]');
  }

  _bindEvents() {
    // Step 1 + 2: option cards auto-advance on click
    this.root.querySelectorAll('[data-panel="1"] .wleb-estimateur__option').forEach((btn) => {
      btn.addEventListener('click', () => {
        this._selectOption(btn, 1);
        this.choices.type = btn.getAttribute('data-value');
        this._goTo(2);
      });
    });

    this.root.querySelectorAll('[data-panel="2"] .wleb-estimateur__option').forEach((btn) => {
      btn.addEventListener('click', () => {
        this._selectOption(btn, 2);
        this.choices.ambition = btn.getAttribute('data-value');
        this._goTo(3);
      });
    });

    // Step 3: "Voir mon estimation" button
    const nextBtn = this.root.querySelector('[data-next="4"]');
    if (nextBtn) {
      nextBtn.addEventListener('click', () => {
        this._collectFeatures();
        this._calculatePrice();
        this._goTo(4);
      });
    }

    // Back button
    if (this.backBtn) {
      this.backBtn.addEventListener('click', () => {
        if (this.currentStep > 1) {
          this._goTo(this.currentStep - 1);
        }
      });
    }

    // Step 4 form submission (reuse LeadForm logic)
    if (this.form) {
      this.form.addEventListener('submit', (e) => this._handleSubmit(e));
    }
  }

  _selectOption(btn, step) {
    const panel = this.root.querySelector(`[data-panel="${step}"]`);
    panel.querySelectorAll('.wleb-estimateur__option').forEach((o) => o.removeAttribute('data-selected'));
    btn.setAttribute('data-selected', '');
  }

  _goTo(step) {
    // Hide all panels
    this.panels.forEach((p) => p.removeAttribute('data-state'));

    // Show target panel
    const target = this.root.querySelector(`[data-panel="${step}"]`);
    if (target) target.setAttribute('data-state', 'active');

    // Update step indicators
    this.indicators.forEach((ind) => {
      const s = parseInt(ind.getAttribute('data-step-indicator'), 10);
      if (s < step) {
        ind.setAttribute('data-state', 'completed');
      } else if (s === step) {
        ind.setAttribute('data-state', 'active');
      } else {
        ind.removeAttribute('data-state');
      }
    });

    // Show/hide back button
    if (this.backBtn) {
      this.backBtn.hidden = step <= 1;
    }

    this.currentStep = step;
  }

  _collectFeatures() {
    this.choices.features = [];
    this.root.querySelectorAll('[data-panel="3"] input[type="checkbox"]:checked').forEach((cb) => {
      this.choices.features.push({
        name: cb.value,
        cost: parseInt(cb.getAttribute('data-cost') || '0', 10),
      });
    });
  }

  _calculatePrice() {
    const type     = this.choices.type || 'vitrine';
    const ambition = this.choices.ambition || 'essentiel';
    const base     = Estimateur.PRICING[type]?.[ambition] || [2500, 5000];

    let extraMin = 0;
    let extraMax = 0;
    this.choices.features.forEach((f) => {
      extraMin += Math.round(f.cost * 0.8);
      extraMax += f.cost;
    });

    const min = base[0] + extraMin;
    const max = base[1] + extraMax;

    if (this.priceMin) this.priceMin.textContent = min.toLocaleString('fr-FR');
    if (this.priceMax) this.priceMax.textContent = max.toLocaleString('fr-FR');

    // Build summary for hidden field
    const featureNames = this.choices.features.map((f) => f.name).join(', ') || 'aucune';
    const summary = `Type: ${type} | Ambition: ${ambition} | Options: ${featureNames} | Estimation: ${min}-${max} EUR HT`;
    if (this.summaryEl) this.summaryEl.value = summary;
  }

  async _handleSubmit(event) {
    event.preventDefault();

    const submitBtn = this.form.querySelector('.wleb-lead-form__submit');
    const feedback  = this.form.querySelector('[data-lead-feedback]');

    if (submitBtn?.getAttribute('data-state') === 'loading') return;

    const name  = this.form.querySelector('[name="lead_name"]');
    const phone = this.form.querySelector('[name="lead_phone"]');

    if (!name?.value.trim() || !phone?.value.trim()) {
      this._showFeedback(feedback, 'Veuillez remplir votre nom et téléphone.', 'error');
      return;
    }

    if (submitBtn) submitBtn.setAttribute('data-state', 'loading');
    if (feedback) feedback.hidden = true;

    const ajaxUrl = this.root.getAttribute('data-ajax-url') || '/wp-admin/admin-ajax.php';

    try {
      const formData = new FormData(this.form);
      const response = await fetch(ajaxUrl, { method: 'POST', body: formData });
      const result   = await response.json();

      if (result.success) {
        this._showFeedback(feedback, result.data?.message || 'Merci ! Votre devis arrive sous 24h.', 'success');
        this.form.reset();

        if (typeof gtag === 'function') {
          gtag('event', 'generate_lead', {
            event_category: 'estimateur',
            event_label: this.summaryEl?.value || '',
          });
        }
      } else {
        this._showFeedback(feedback, result.data?.message || 'Erreur. Appelez-nous au 06 40 65 57 42.', 'error');
      }
    } catch {
      this._showFeedback(feedback, 'Erreur réseau. Appelez-nous au 06 40 65 57 42.', 'error');
    }

    if (submitBtn) submitBtn.removeAttribute('data-state');
  }

  _showFeedback(el, message, type) {
    if (!el) return;
    el.textContent = message;
    el.setAttribute('data-type', type);
    el.hidden = false;
  }

  destroy() {}
}
